const BASE = '/api';

async function request(path, options = {}) {
  const { method = 'GET', body, raw = false } = options;
  const config = {
    method,
    headers: {},
    credentials: 'same-origin',
  };

  if (body !== undefined) {
    config.headers['Content-Type'] = 'application/json';
    config.body = JSON.stringify(body);
  }

  const res = await fetch(`${BASE}${path}`, config);

  if (raw) return res;

  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.error || `Fehler ${res.status}`);
  }

  const text = await res.text();
  return text ? JSON.parse(text) : null;
}

export const api = {
  get: (path) => request(path),
  post: (path, body) => request(path, { method: 'POST', body }),
  put: (path, body) => request(path, { method: 'PUT', body }),
  del: (path) => request(path, { method: 'DELETE' }),
  raw: (path, opts) => request(path, { ...opts, raw: true }),

  admin: {
    login: (username, password) => request('/admin/login', { method: 'POST', body: { username, password } }),
    logout: () => request('/admin/logout', { method: 'POST' }),
    me: () => request('/admin/me'),
    changePassword: (data) => request('/admin/password/change', { method: 'POST', body: data }),

    // Events (multi-event support)
    getEvents: () => request('/admin/events'),
    createEvent: (data) => request('/admin/events', { method: 'POST', body: data }),
    getEvent: (id) => request(`/admin/events/${id}`),
    updateEvent: (id, data) => request(`/admin/events/${id}`, { method: 'PUT', body: data }),
    deleteEvent: (id) => request(`/admin/events/${id}`, { method: 'DELETE' }),
    activateEvent: (id) => request(`/admin/events/${id}/activate`, { method: 'POST' }),

    // Event-scoped bands
    getEventBands: (eid) => request(`/admin/events/${eid}/bands`),
    createEventBand: (eid, data) => request(`/admin/events/${eid}/bands`, { method: 'POST', body: data }),
    updateEventBand: (eid, id, data) => request(`/admin/events/${eid}/bands/${id}`, { method: 'PUT', body: data }),
    deleteEventBand: (eid, id) => request(`/admin/events/${eid}/bands/${id}`, { method: 'DELETE' }),
    exportEventBands: (eid) => request(`/admin/events/${eid}/bands/export`),
    importEventBands: (eid, bands) => request(`/admin/events/${eid}/bands/import`, { method: 'POST', body: { bands } }),
    copyBandToEvent: (eid, id, targetEventId) => request(`/admin/events/${eid}/bands/${id}/copy-to-event`, { method: 'POST', body: { targetEventId } }),

    // Event-scoped applications
    getEventApplications: (eid, params) => {
      const qs = new URLSearchParams(params).toString();
      return request(`/admin/events/${eid}/applications?${qs}`);
    },
    createEventApplication: (eid, data) => request(`/admin/events/${eid}/applications`, { method: 'POST', body: data }),
    updateEventApplication: (eid, id, data) => request(`/admin/events/${eid}/applications/${id}`, { method: 'PUT', body: data }),
    deleteEventApplication: (eid, id) => request(`/admin/events/${eid}/applications/${id}`, { method: 'DELETE' }),
    bulkStatusEventApplications: (eid, ids, status) => request(`/admin/events/${eid}/applications/bulk-status`, { method: 'POST', body: { ids, status } }),
    uploadEventDemoFiles: (eid, id, files) => {
      const fd = new FormData();
      files.forEach((f) => fd.append('demoFiles', f));
      return fetch(`/api/admin/events/${eid}/applications/upload-files/${id}`, { method: 'POST', body: fd, credentials: 'same-origin' })
        .then(async (res) => { if (!res.ok) { const d = await res.json().catch(() => ({})); throw new Error(d.error || `Fehler ${res.status}`); } return res.json(); });
    },
    deleteEventDemoFile: (eid, id, fileIndex) => request(`/admin/events/${eid}/applications/${id}/files/${fileIndex}`, { method: 'DELETE' }),
    downloadEventDemoFile: (eid, id, fileIndex) => request(`/admin/events/${eid}/applications/${id}/files/${fileIndex}`, { raw: true }),
    copyApplicationToEvent: (eid, id, targetEventId) => request(`/admin/events/${eid}/applications/${id}/copy-to-event`, { method: 'POST', body: { targetEventId } }),

    getGallery: () => request('/admin/gallery'),
    createGalleryItem: (data) => request('/admin/gallery', { method: 'POST', body: data }),
    updateGalleryItem: (id, data) => request(`/admin/gallery/${id}`, { method: 'PUT', body: data }),
    deleteGalleryItem: (id) => request(`/admin/gallery/${id}`, { method: 'DELETE' }),

    getVideos: () => request('/admin/videos'),
    createVideo: (data) => request('/admin/videos', { method: 'POST', body: data }),
    updateVideo: (id, data) => request(`/admin/videos/${id}`, { method: 'PUT', body: data }),
    deleteVideo: (id) => request(`/admin/videos/${id}`, { method: 'DELETE' }),

    getNewsletter: (params) => {
      const qs = new URLSearchParams(params).toString();
      return request(`/admin/newsletter?${qs}`);
    },
    deleteSubscriber: (id) => request(`/admin/newsletter/${id}`, { method: 'DELETE' }),

    getSettings: () => request('/admin/settings'),
    updateSettings: (data) => request('/admin/settings', { method: 'PUT', body: data }),
    exportAll: () => request('/admin/settings/export'),
    importAll: (data) => request('/admin/settings/import', { method: 'POST', body: data }),
    resetAll: () => request('/admin/settings/reset', { method: 'POST', body: { confirm: 'RESET' } }),

    getUsers: () => request('/admin/users'),
    createUser: (data) => request('/admin/users', { method: 'POST', body: data }),
    updateUser: (id, data) => request(`/admin/users/${id}`, { method: 'PUT', body: data }),
    deleteUser: (id) => request(`/admin/users/${id}`, { method: 'DELETE' }),
    resetUserPassword: (id, newPassword) => request(`/admin/users/${id}/reset-password`, { method: 'POST', body: { newPassword } }),
  },

  bandProfile: {
    get: (token) => request(`/band-profil/${token}`),
    save: (token, data) => request(`/band-profil/${token}`, { method: 'PUT', body: data }),
  },

  public: {
    getEvent: () => request('/public/event'),
    getBands: () => request('/public/bands'),
    getGallery: () => request('/public/gallery'),
    getVideos: () => request('/public/videos'),
    getSettings: () => request('/public/settings'),
    submitApplication: (data) => request('/public/applications', { method: 'POST', body: data }),
    subscribe: (email) => request('/public/newsletter', { method: 'POST', body: { email } }),
  },
};
